import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import { useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";

import { useForm } from "@mantine/form";
import { Button, Stack } from "@mantine/core";

import { ContactAndBillingInput } from "@components/ContactAndBillingInput/ContactAndBillingInput";

import { Draft } from "@utils/Draft.util";
import { IDemoRegistrationForm } from "@models/DemoRegistrationForm.model";
import { toContactAndBillingInputProps } from "@utils/toContactAndBillingInputProps.util";
import { contactAndBillingDraftToInitialValues } from "@utils/contactAndBillingDraftToInitialValues.util";
import { currentStepAtom } from "../atoms/currentStep.atom";
import { useLanguageCode } from "@hooks/useLanguageCode";
import { IStepRef } from "@models/Step.model";

export const Step1 = forwardRef<
  IStepRef<IDemoRegistrationForm>,
  {
    readonly?: boolean;
    isSavePending?: boolean;
    draft?: Draft<IDemoRegistrationForm>;
    onChange?: (change: Draft<IDemoRegistrationForm>) => void;
  }
>(
  (
    {
      readonly = false,
      isSavePending = false,
      draft = {},
      onChange = () => {},
    },
    ref
  ) => {
    const setCurrentStep = useSetAtom(currentStepAtom);
    const { t } = useTranslation();
    const lc = useLanguageCode();
    const form = useForm({
      initialValues: {
        ...contactAndBillingDraftToInitialValues(draft, lc),
      },
    });

    const $toRefs = { onChange };
    const toRefs = useRef($toRefs);
    useEffect(() => {
      toRefs.current = $toRefs;
    });

    useEffect(() => {
      toRefs.current.onChange(form.values);
    }, [form.values]);

    useImperativeHandle(ref, () => ({
      getValues: () => form.values,
      isDirty: () => form.isDirty(),
      resetDirty: () => form.resetDirty(),
    }));

    return (
      <form onSubmit={form.onSubmit(() => setCurrentStep((pv) => pv + 1))}>
        <Stack>
          <ContactAndBillingInput {...toContactAndBillingInputProps(form)} />
          {!readonly && (
            <Button type="submit" loading={isSavePending}>
              {isSavePending ? `${t("Saving")}...` : t("Continue")}
            </Button>
          )}
        </Stack>
      </form>
    );
  }
);
